import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { convertToServerPaginationIfNot, getErrorResponse } from 'legacy/lut/functions'

import { IForm } from 'core-components/classes/models/IForm'
import { IDataProviderFilter, IPaging } from 'core-components/types'
import { Direction } from 'core-components/types/ICrudHelpers'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'
import { ProcedureInnerStorageFetcher } from 'core/types/ProcedureInnerStorageFetcher'

import { ThirdPartyLogApplication } from '../classes/models/thirdPartyLog'

type TFetchLogListResponse = { items: Record<string, any>[] }
export type TFetchApplicationLogsByRequestIdParams = {
  requestId: number
  application: ThirdPartyLogApplication
}
export type TListApplicationLogsByPartnerIdParams = {
  partnerId: string
  application: ThirdPartyLogApplication
  kendoConditions?: IDataProviderFilter
}

type TListParams = {
  order?: { column: string; dir?: Direction }[]
  pagination: IPaging
}

export enum ThirdPartyTypeCrudCacheKeys {
  PartnerOnly = 'PartnerOnly',
  PartnerOrRequest = 'PartnerOrRequest',
}

class ThirdPartyLogsApi {
  public getTypeCrud(cacheKey?: ThirdPartyTypeCrudCacheKeys) {
    return CrudStore.get(CrudStoreItem.ThirdPartyType, cacheKey)
  }

  public invalidateTypeCrud(cacheKey?: ThirdPartyTypeCrudCacheKeys, triggerSubscribers?: boolean) {
    CrudStore.get(CrudStoreItem.ThirdPartyType, cacheKey).removeQueries(triggerSubscribers)
  }

  public getCrudApplicationLogsByRequestId(params: TFetchApplicationLogsByRequestIdParams) {
    return new Crud({
      fetcher: new ProcedureInnerStorageFetcher<IForm>('id', {
        readFromTo: async (filter, sort, paging) => {
          const response = await this.fetchApplicationLogsByRequestId(params, {
            pagination: convertToServerPaginationIfNot(paging),
            order: sort ? [sort].flat().map((i) => ({ dir: i.dir, column: i.field })) : undefined,
          })

          return { Data: response.data.items }
        },
      }),
      keyField: 'id',
      defaultPaging: {
        startPage: 1,
        page: 1,
        pageSize: 10,
      },
      defaultSort: [{ field: 'Created', dir: 'desc' }],
    })
  }

  public getCrudApplicationLogsByPartnerId(
    params: Omit<TListApplicationLogsByPartnerIdParams, 'kendoConditions'>
  ) {
    return new Crud({
      fetcher: new ProcedureInnerStorageFetcher<IForm>('id', {
        readFromTo: async (filter, sort, paging) => {
          const response = await this.fetchApplicationLogsByPartnerId(
            { ...params, kendoConditions: filter },
            {
              pagination: convertToServerPaginationIfNot(paging),
              order: sort ? [sort].flat().map((i) => ({ dir: i.dir, column: i.field })) : undefined,
            }
          )

          return { Data: response.data.items }
        },
      }),
      keyField: 'id',
      defaultPaging: {
        startPage: 1,
        page: 1,
        pageSize: 10,
      },
      defaultSort: [{ field: 'Created', dir: 'desc' }],
    })
  }

  public async fetchType(partnerId: string, requestId?: number) {
    const wsClient = WebSocketStore.get('onboarding')
    const failureMessage = 'Failed to fetch type'
    const payload = { partnerId, requestId }

    let response
    try {
      response = await wsClient.call<{ type: string | null }>({
        service: 'third-party',
        action: 'fetch-type',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      response = getErrorResponse<{ type: string | null }>(e.message || failureMessage, {
        type: null,
      })
    }

    return {
      ...response,
      message: response.message || (response.status === 'success' ? '' : failureMessage),
    }
  }

  public async fetchApplicationsByRequestId(requestId: number) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{ items: ThirdPartyLogApplication[] }>({
      service: 'third-party-logs',
      action: 'fetch-applications-by-request',
      payload: { requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Fail')
    }

    return response.data.items
  }

  public async fetchApplicationsByPartnerId(partnerId: string) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{ items: ThirdPartyLogApplication[] }>({
      service: 'third-party-logs',
      action: 'fetch-applications-by-partner',
      payload: { partnerId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Fail')
    }

    return response.data.items
  }

  public async fetchApplicationLogsByRequestId(
    fetchParams: TFetchApplicationLogsByRequestIdParams,
    listParams: TListParams
  ) {
    const wsClient = WebSocketStore.get('onboarding')
    const failureMessage = 'Failed to fetch logs'
    const payload = { ...fetchParams, ...listParams.pagination, order: listParams.order }

    let response
    try {
      response = await wsClient.call<TFetchLogListResponse>({
        service: 'third-party-logs',
        action: 'fetch-for-application-by-request',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      response = getErrorResponse<TFetchLogListResponse>(e.message || failureMessage, {
        items: [],
      })
    }

    return {
      ...response,
      message: response.message || (response.status === 'success' ? '' : failureMessage),
    }
  }

  public async fetchApplicationLogsByPartnerId(
    fetchParams: TListApplicationLogsByPartnerIdParams,
    listParams: TListParams
  ) {
    const wsClient = WebSocketStore.get('onboarding')
    const failureMessage = 'Failed to fetch logs'
    const payload = { ...fetchParams, ...listParams.pagination, order: listParams.order }

    let response
    try {
      response = await wsClient.call<TFetchLogListResponse>({
        service: 'third-party-logs',
        action: 'fetch-for-application-by-partner',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      response = getErrorResponse<TFetchLogListResponse>(e.message || failureMessage, {
        items: [],
      })
    }

    return {
      ...response,
      message: response.message || (response.status === 'success' ? '' : failureMessage),
    }
  }
}

export const thirdPartyLogsApi = new ThirdPartyLogsApi()
